/** Single-flow chart: { t, type: 'tap'|'hold'|'double'|'scrub', duration?, gold? }. */

/** Seconds reserved after a double's first tap for its second tap. */
export const DOUBLE_WINDOW = 0.3;

function beatTime(bpm, beat) {
  return +((beat * 60) / bpm).toFixed(4);
}

function tap(notes, bpm, beat) {
  notes.push({ t: beatTime(bpm, beat), type: 'tap' });
}

function double(notes, bpm, beat) {
  notes.push({ t: beatTime(bpm, beat), type: 'double' });
}

function hold(notes, bpm, beat, durationBeats) {
  notes.push({
    t: beatTime(bpm, beat),
    type: 'hold',
    duration: beatTime(bpm, durationBeats)
  });
}

function scrub(notes, bpm, beat, durationBeats) {
  notes.push({
    t: beatTime(bpm, beat),
    type: 'scrub',
    duration: beatTime(bpm, durationBeats)
  });
}

function taps(notes, bpm, start, count, step = 1) {
  for (let i = 0; i < count; i++) tap(notes, bpm, start + i * step);
}

/**
 * Generic chart for any song, scaled to its BPM and duration.
 *
 * Fast sections use eighth notes when they stay >= 249 ms apart
 * (bpm <= ~120), otherwise 0.75-beat "gallop" patterns.
 * Sections cycle (phrases, holds, build, breathe, drop, reset, frenzy)
 * until the song runs out, then a final sustained hold leaves an audio tail.
 */
export function buildAutoChart(bpm, duration) {
  const notes = [];
  const fast = (60 / bpm) * 0.5 >= 0.249 ? 0.5 : 0.75;
  const beatsTotal = Math.max(48, Math.floor(((duration - 4) * bpm) / 60));
  let b = 4;

  // Intro: readable quarter-note pulse.
  taps(notes, bpm, b, 16, 1);
  b += 16;

  const sections = [
    { beats: 32, write: (s) => { for (let p = 0; p < 4; p++) taps(notes, bpm, s + p * 8, 6, 1); } },
    { beats: 16, write: (s) => { for (let i = 0; i < 4; i++) hold(notes, bpm, s + i * 4, 2); } },
    {
      beats: 16,
      write: (s) => {
        for (let p = 0; p < 4; p++) {
          if (fast === 0.5) taps(notes, bpm, s + p * 4, 6, 0.5);
          else { taps(notes, bpm, s + p * 4, 4, 0.75); tap(notes, bpm, s + p * 4 + 3); }
        }
      }
    },
    { beats: 16, write: (s) => { hold(notes, bpm, s, 4); hold(notes, bpm, s + 8, 4); } },
    {
      beats: 32,
      write: (s) => {
        for (let p = 0; p < 8; p++) {
          if (fast === 0.5) taps(notes, bpm, s + p * 4, 6, 0.5);
          else { taps(notes, bpm, s + p * 4, 4, 0.75); tap(notes, bpm, s + p * 4 + 3); }
        }
      }
    },
    { beats: 16, write: (s) => { taps(notes, bpm, s, 8, 1); hold(notes, bpm, s + 8, 2); hold(notes, bpm, s + 12, 2); } },
    {
      beats: 20,
      write: (s) => {
        for (let p = 0; p < 5; p++) {
          if (fast === 0.5) taps(notes, bpm, s + p * 4, 7, 0.5);
          else taps(notes, bpm, s + p * 4, 5, 0.75);
        }
      }
    }
  ];

  // Cycle sections until the song runs out (final hold needs 4 beats).
  for (let index = 0; ; index++) {
    const section = sections[index % sections.length];
    if (b + section.beats > beatsTotal - 4) break;
    section.write(b);
    b += section.beats;
  }

  // Fill the remaining tail with readable 6-on / 2-off quarter phrases.
  while (b + 8 <= beatsTotal - 4) {
    taps(notes, bpm, b, 6, 1);
    b += 8;
  }

  // Final sustained note, leaving an audio tail.
  hold(notes, bpm, b, 4);

  return finalize(notes, { autoGold: true });
}

/**
 * Difficulty profiles: the density changes, notes always stay snapped
 * to the song's real onset slots.
 *   - quietTaps:  taps on quiet bars' strongest accents (easy breathes);
 *   - midBeats / midOffbeats: beats used in mid-energy bars (+ N offbeats);
 *   - highBeats / offbeats / offbeatFloor: beats and accented offbeats on
 *     high-energy bars (lower floor = more offbeats kept);
 *   - dense:      full drop patterns (easy keeps drops readable);
 *   - doubleEvery / scrubEvery: 1 drop bar out of N gets a double / a scrub;
 *   - dropTail:   drops also fill the 3.5 pickup into the next bar;
 *   - holdTail:   a tap right after each hold (no rest on hard).
 *
 * Normal is the old hard; hard pushes further on every axis.
 */
const PROFILES = {
  easy: { quietTaps: 0, midBeats: 2, midOffbeats: 0, highBeats: 3, offbeats: 0, offbeatFloor: 0.45, dense: false, doubleEvery: 0, scrubEvery: 0, dropTail: false, holdTail: false, minHoldBars: 6 },
  normal: { quietTaps: 2, midBeats: 4, midOffbeats: 1, highBeats: 4, offbeats: 3, offbeatFloor: 0.35, dense: true, doubleEvery: 1, scrubEvery: 4, dropTail: false, holdTail: false, minHoldBars: 6 },
  hard: { quietTaps: 3, midBeats: 4, midOffbeats: 2, highBeats: 4, offbeats: 3, offbeatFloor: 0.28, dense: true, doubleEvery: 1, scrubEvery: 3, dropTail: true, holdTail: true, scrubTail: true, minHoldBars: 3 }
};

/**
 * Chart driven by the song's real energy map (see scripts/analyze-song.py).
 * Bars are ranked against the song's own distribution (pop masters are
 * compressed, absolute thresholds would be blind):
 *   - quiet quartile: one tap on the strongest accent, or a breath;
 *   - mid: taps snapped to the strongest beats;
 *   - high: full beats plus accented offbeats;
 *   - top (drops): eighths (or gallop above ~120 BPM) on real hits,
 *     spiced with doubles and zigzag scrubs so the player flares up
 *     with the music.
 * Sustained low-punch bars become holds; golds land on the strongest peaks.
 * Falls back to buildAutoChart when no analysis is available.
 */
export function buildMusicalChart(bpm, duration, analysis, difficulty = 'normal') {
  if (!analysis?.bars?.length || !analysis?.slots?.length) {
    return buildAutoChart(bpm, duration);
  }
  const profile = PROFILES[difficulty] || PROFILES.normal;

  const period = 60 / bpm;
  const slotsPerBeat = analysis.slotsPerBeat || 2;
  const beatsPerBar = analysis.beatsPerBar || 4;
  const eighthOk = period * 0.5 >= 0.249;
  const { bars, slots } = analysis;
  const punch = analysis.punch || bars;

  const strengthAt = (beat) => slots[Math.round(beat * slotsPerBeat)] ?? 0;

  // Percentile rank of each bar in the song's own energy distribution.
  const mix = bars.map((energy, i) => 0.4 * energy + 0.6 * (punch[i] ?? energy));
  const sortedMix = [...mix].sort((a, b) => a - b);
  const rankOf = (value) => {
    let low = 0;
    while (low < sortedMix.length && sortedMix[low] < value) low++;
    return sortedMix.length > 1 ? low / (sortedMix.length - 1) : 1;
  };
  const punchSorted = [...punch].sort((a, b) => a - b);
  const punchRankOf = (value) => {
    let low = 0;
    while (low < punchSorted.length && punchSorted[low] < value) low++;
    return punchSorted.length > 1 ? low / (punchSorted.length - 1) : 1;
  };

  // Hold bars: sustained energy but little percussion (pads, held vocals).
  // Adaptive floor: even on very percussive songs, some hold bars remain so
  // nebulae keep igniting — hard keeps fewer of them (holds are restful).
  const holdCandidates = [];
  for (let bar = 1; bar < bars.length; bar++) {
    if (rankOf(bars[bar]) >= 0.4) holdCandidates.push(bar);
  }
  holdCandidates.sort((a, b) => punchRankOf(punch[a]) - punchRankOf(punch[b]));
  const holdBars = new Set();
  const sparseFloor = profile.minHoldBars <= 3 ? 0.2 : 0.35;
  for (const bar of holdCandidates) {
    const sparse = punchRankOf(punch[bar]) <= sparseFloor;
    if (!sparse && holdBars.size >= profile.minHoldBars) break;
    holdBars.add(bar);
  }

  const notes = [];
  const beatsTotal = Math.floor(((duration - 1) * bpm) / 60);
  const lastPatternBeat = beatsTotal - 8;

  const strongestBeats = (barStart, count) => {
    const beats = [0, 1, 2, 3].map((beatOffset) => ({
      beatOffset,
      strength: strengthAt(barStart + beatOffset)
    }));
    beats.sort((a, b) => b.strength - a.strength);
    return beats.slice(0, count).map((entry) => entry.beatOffset).sort((a, b) => a - b);
  };

  let dropIndex = 0;
  for (let bar = 1; bar < bars.length; bar++) {
    const barStart = bar * beatsPerBar;
    if (barStart + beatsPerBar > lastPatternBeat) break;
    if (bars[bar] < 0.12 && punch[bar] < 0.12) continue; // actual silence: breathe

    const rank = rankOf(mix[bar]);

    // Sustained pad/vocal bar with little percussion: a 2-beat hold.
    if (holdBars.has(bar)) {
      hold(notes, bpm, barStart, 2);
      // Hard: no rest — a tap right after the hold when the music backs it.
      if (profile.holdTail && strengthAt(barStart + 3) >= 0.3) {
        tap(notes, bpm, barStart + 3);
      }
      continue;
    }

    if (rank < 0.25) {
      // Quiet: taps on the strongest accents (easy breathes, hard stays busy).
      for (const beatOffset of strongestBeats(barStart, profile.quietTaps)) {
        if (strengthAt(barStart + beatOffset) >= 0.25) tap(notes, bpm, barStart + beatOffset);
      }
    } else if (rank < 0.5) {
      // Mid: the strongest beats (2 easy / 4 normal+hard, plus N offbeats).
      for (const beatOffset of strongestBeats(barStart, profile.midBeats)) {
        tap(notes, bpm, barStart + beatOffset);
      }
      if (profile.midOffbeats > 0 && eighthOk) {
        const best = [0.5, 1.5, 2.5]
          .map((half) => ({ half, strength: strengthAt(barStart + half) }))
          .filter((entry) => entry.strength >= 0.4)
          .sort((a, b) => b.strength - a.strength)
          .slice(0, profile.midOffbeats);
        for (const { half } of best) tap(notes, bpm, barStart + half);
      }
    } else if (rank < 0.8) {
      // High: beats plus the strongest usable offbeats.
      if (profile.highBeats >= 4) taps(notes, bpm, barStart, 4, 1);
      else for (const beatOffset of strongestBeats(barStart, profile.highBeats)) tap(notes, bpm, barStart + beatOffset);
      if (profile.offbeats > 0 && eighthOk) {
        const offbeats = [0.5, 1.5, 2.5]
          .map((half) => ({ half, strength: strengthAt(barStart + half) }))
          .filter((entry) => entry.strength >= profile.offbeatFloor)
          .sort((a, b) => b.strength - a.strength)
          .slice(0, profile.offbeats);
        for (const { half } of offbeats) tap(notes, bpm, barStart + half);
      } else if (profile.offbeats > 0) {
        // Fast tempo: swap the last beat for a 2.75 pickup (gaps stay >= 0.75 beat).
        if (strengthAt(barStart + 2.75) >= 0.45) {
          notes.pop();
          tap(notes, bpm, barStart + 2.75);
        }
      }
    } else {
      // Drop: dense pattern on real hits, spiced with scrubs and doubles.
      dropIndex++;
      if (profile.scrubEvery && dropIndex % profile.scrubEvery === 0) {
        if (profile.scrubTail) {
          // Hard: shorter scrub chained straight into taps — no breather.
          scrub(notes, bpm, barStart, 2);
          if (strengthAt(barStart + 3) >= 0.2) tap(notes, bpm, barStart + 3);
          if (eighthOk && strengthAt(barStart + 3.5) >= 0.3) tap(notes, bpm, barStart + 3.5);
        } else {
          // Zigzag scrub riding the drop: rub for 3 beats.
          scrub(notes, bpm, barStart, 3);
        }
        continue;
      }
      const pattern = profile.dense
        ? (eighthOk
          ? (profile.dropTail ? [0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5] : [0, 0.5, 1, 1.5, 2, 2.5, 3])
          : (profile.dropTail ? [0, 0.75, 1.5, 2.25, 3, 3.75] : [0, 0.75, 1.5, 2.25, 3]))
        : [0, 1, 2, 3];
      // The double lands on the pattern position closest to the bar's strongest beat.
      let doubleAt = -1;
      if (profile.doubleEvery && dropIndex % profile.doubleEvery === 0) {
        const [strongest] = strongestBeats(barStart, 1);
        doubleAt = pattern.reduce(
          (best, offset) => Math.abs(offset - strongest) < Math.abs(best - strongest) ? offset : best,
          pattern[0]
        );
      }
      const period = 60 / bpm;
      for (const beatOffset of pattern) {
        if (strengthAt(barStart + beatOffset) < 0.08) continue;
        if (beatOffset === doubleAt) {
          double(notes, bpm, barStart + beatOffset);
        } else {
          // Clear the runway right before the double so finalize keeps it.
          if (doubleAt >= 0 && beatOffset < doubleAt && (doubleAt - beatOffset) * period < 0.26) continue;
          tap(notes, bpm, barStart + beatOffset);
        }
      }
      if (!profile.dense && eighthOk) {
        const [bestOff] = [0.5, 1.5, 2.5].sort((a, b) => strengthAt(barStart + b) - strengthAt(barStart + a));
        if (strengthAt(barStart + bestOff) >= 0.5) tap(notes, bpm, barStart + bestOff);
      }
    }
  }

  // Final sustained note, leaving an audio tail.
  hold(notes, bpm, beatsTotal - 4, 4);

  const kept = finalize(notes);
  markGoldOnPeaks(kept, bpm, duration, strengthAt);
  return kept;
}

/** Golds on the strongest musical peaks, spaced >= 4 s, 12-30 per song. */
function markGoldOnPeaks(notes, bpm, duration, strengthAt) {
  const period = 60 / bpm;
  const candidates = notes
    .filter((note) => note.type === 'tap')
    .map((note) => ({ note, strength: strengthAt(note.t / period) }))
    .sort((a, b) => b.strength - a.strength);

  const target = Math.max(12, Math.min(30, Math.round(duration / 8)));
  const goldTimes = [];
  for (const { note } of candidates) {
    if (goldTimes.length >= target) break;
    if (goldTimes.some((t) => Math.abs(t - note.t) < 4)) continue;
    note.gold = true;
    goldTimes.push(note.t);
  }
}

/** Seconds a note occupies after its start (next note must wait 249 ms more). */
function occupancy(note) {
  if (note.type === 'double') return DOUBLE_WINDOW;
  if (note.type === 'hold' || note.type === 'scrub') return note.duration;
  return 0;
}

function finalize(notes, { autoGold = false } = {}) {
  notes.sort((a, b) => a.t - b.t);
  let prevEnd = -Infinity;
  const kept = notes.filter((note) => {
    if (!Number.isFinite(note.t)) return false;
    if (!['tap', 'hold', 'double', 'scrub'].includes(note.type)) return false;
    if (['hold', 'scrub'].includes(note.type) && (!Number.isFinite(note.duration) || note.duration <= 0)) return false;
    if (note.t - prevEnd < 0.249) return false;
    prevEnd = note.t + occupancy(note);
    return true;
  });

  if (autoGold) {
    // Deterministic golden taps: roughly 1 in 12, same for every run.
    let tapIndex = 0;
    for (const note of kept) {
      if (note.type !== 'tap') continue;
      if (tapIndex % 12 === 7) note.gold = true;
      tapIndex++;
    }
  }
  return kept;
}
